import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Briefcase, ChevronDown, ChevronUp, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePageContent } from '../hooks/usePageContent';

const API = process.env.REACT_APP_BACKEND_URL;

const REC_DEFAULTS = {
  hero_title: 'Rejoignez-nous',
  hero_subtitle: "Intégrez une entreprise dynamique et participez à des projets d'envergure. SOPREL Groupe recrute des talents passionnés par la construction.",
  advantages_title: 'Une entreprise où il fait bon travailler',
  advantages_text: "Chez SOPREL, nous croyons que la réussite de l'entreprise passe par l'épanouissement de nos collaborateurs.",
  spontaneous_title: 'Candidature spontanée',
  spontaneous_text: "Nous sommes néanmoins toujours à la recherche de talents prêts à rejoindre l'aventure GROUPE SOPREL. N'hésitez pas à nous adresser votre candidature spontanée : toutes les candidatures sont examinées avec soin.",
};

const PageHeader = ({ c }) => {
  return (
    <section className="pt-32 pb-20 bg-[#0e1c3f]" data-testid="recrutement-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-[#0078b7] font-bold tracking-[0.2em] uppercase text-sm mb-4">
            Carrières
          </p>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white uppercase tracking-tight mb-6">
            {c.hero_title}
          </h1>
          <p className="text-slate-300 text-lg leading-relaxed">
            {c.hero_subtitle}
          </p>
        </div>
      </div>
    </section>
  );
};

const BANNER_PHOTOS = [
  "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/99fiezuz_SOPREL_V1_51310_0127.jpg",
  "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/l6v8xoqj_SOPREL_V1_51310_0128.jpg",
  "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/1dyktg8o_SOPREL_V1_51310_0131.jpg",
  "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/el4kbwth_SOPREL_V1_77130_0054.JPG",
  "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/eilnxfcg_SOPREL_V1_77130_0070.JPG",
  "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/6r7w1fi9_SOPREL_V1_51310_0126.jpg",
  "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/j7u2boo6_SOPREL_V1_51310_0111.jpg",
  "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/37enyx1x_SOPREL_V1_51310_0110.jpg",
  "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/zgq6hlrh_SOPREL_V1_51310_0097.jpg",
  "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/kq25xfxy_SOPREL_V1_51310_0095.jpg",
  "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/3gwnwrq5_SOPREL_V1_51310_0080.jpg",
  "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/mfajgbpt_SOPREL_V1_27600_0055.JPG",
  "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/ehu86bsv_SOPREL_V1_27600_0013.JPG",
  "https://customer-assets.emergentagent.com/job_soprel-build/artifacts/ix5kt9eg_SOPREL_V1_27600_0001.JPG",
];

const PhotoBanner = () => {
  // Duplicate array for seamless infinite loop
  const photos = [...BANNER_PHOTOS, ...BANNER_PHOTOS];

  return (
    <section className="py-0 bg-[#0e1c3f] overflow-hidden" data-testid="photo-banner">
      <div className="relative">
        {/* Fade edges */}
        <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-[#0e1c3f] to-transparent z-10" />
        <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-[#0e1c3f] to-transparent z-10" />

        <div className="flex animate-marquee" style={{ width: 'max-content' }}>
          {photos.map((src, i) => (
            <div key={i} className="flex-shrink-0 w-56 h-40 sm:w-72 sm:h-48 lg:w-80 lg:h-56 mx-1.5 sm:mx-2 overflow-hidden group">
              <img
                src={src}
                alt={`SOPREL Production ${i + 1}`}
                className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const WhyJoinSection = ({ c }) => {
  const reasons = [
    {
      title: 'Innovation',
      description: 'Travaillez sur des technologies de pointe dans le secteur du béton préfabriqué.'
    },
    {
      title: 'Formation',
      description: 'Bénéficiez de programmes de formation continue pour développer vos compétences.'
    },
    {
      title: 'Évolution',
      description: 'Profitez d\'opportunités de carrière au sein d\'un groupe en croissance.'
    },
    {
      title: 'Équilibre',
      description: 'Nous veillons à l\'équilibre vie professionnelle / vie personnelle de nos équipes.'
    }
  ];

  return (
    <section className="py-24 bg-white" data-testid="why-join-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-[#0078b7] font-bold tracking-[0.2em] uppercase text-sm mb-4">
            Pourquoi nous rejoindre
          </p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0e1c3f] uppercase tracking-tight">
            {c.advantages_title}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {reasons.map((reason, index) => (
            <div key={reason.title} className="bg-slate-50 p-8 border border-slate-200">
              <div className="w-10 h-10 bg-[#0078b7]/10 text-[#0078b7] rounded-sm flex items-center justify-center font-heading font-bold text-lg mb-6">
                {String(index + 1).padStart(2, '0')}
              </div>
              <h3 className="font-heading font-bold text-lg text-[#0e1c3f] mb-3">
                {reason.title}
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                {reason.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const JobsSection = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedJob, setExpandedJob] = useState(null);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const { data } = await axios.get(`${API}/api/jobs`);
        setJobs(data);
      } catch (e) {
        console.error('Error fetching jobs:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  const toggleJob = (id) => {
    setExpandedJob(expandedJob === id ? null : id);
  };

  if (loading) {
    return (
      <section className="py-24 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white h-24 animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 bg-slate-50" data-testid="jobs-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-[#0078b7] font-bold tracking-[0.2em] uppercase text-sm mb-4">
            Offres d'emploi
          </p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0e1c3f] uppercase tracking-tight">
            Nos postes ouverts
          </h2>
        </div>

        {jobs.length > 0 ? (
          <div className="space-y-4">
            {jobs.map((job) => (
              <div 
                key={job.id}
                className="bg-white border border-slate-200 overflow-hidden"
                data-testid={`job-${job.id}`}
              >
                <button
                  onClick={() => toggleJob(job.id)}
                  className="w-full px-4 sm:px-8 py-4 sm:py-6 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                >
                  <div className="flex-1">
                    <h3 className="font-heading font-bold text-lg text-[#0e1c3f] mb-2">
                      {job.title}
                    </h3>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {job.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <Briefcase className="h-4 w-4" />
                        {job.contract_type}
                      </span>
                    </div>
                  </div>
                  {expandedJob === job.id ? (
                    <ChevronUp className="h-5 w-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-slate-400" />
                  )}
                </button>

                {expandedJob === job.id && (
                  <div className="px-4 sm:px-8 pb-6 sm:pb-8 border-t border-slate-100">
                    <div className="pt-6">
                      <h4 className="font-heading font-bold text-[#0e1c3f] mb-3">Description</h4>
                      <p className="text-slate-600 mb-6 leading-relaxed">{job.description}</p>

                      {job.requirements && job.requirements.length > 0 && (
                        <>
                          <h4 className="font-heading font-bold text-[#0e1c3f] mb-3">Profil recherché</h4>
                          <ul className="space-y-2 mb-6">
                            {job.requirements.map((req, i) => (
                              <li key={i} className="flex items-start gap-2 text-slate-600">
                                <div className="w-1.5 h-1.5 bg-[#0078b7] rounded-full mt-2" />
                                {req}
                              </li>
                            ))}
                          </ul>
                        </>
                      )}

                      <Button asChild className="bg-[#0e1c3f] hover:bg-[#0e1c3f]/90 text-white rounded-sm">
                        <Link to="/contact">
                          Postuler
                          <Send className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white border border-slate-200 p-8 sm:p-12 text-center">
            <Briefcase className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-xl text-[#0e1c3f] mb-2">
              Pas d'offres disponibles
            </h3>
            <p className="text-slate-600 mb-6">
              Aucune offre d'emploi n'est disponible pour le moment, 
              mais nous sommes toujours à la recherche de talents.
            </p>
            <Button asChild className="bg-[#0e1c3f] hover:bg-[#0e1c3f]/90 text-white rounded-sm">
              <Link to="/contact">Candidature spontanée</Link>
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

const SpontaneousSection = ({ c }) => {
  return (
    <section className="py-24 bg-[#0078b7]" data-testid="spontaneous-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="font-display font-bold text-3xl sm:text-4xl text-white uppercase tracking-tight mb-6">
          {c.spontaneous_title}
        </h2>
        <p className="text-cyan-100 text-lg mb-10 max-w-2xl mx-auto">
          {c.spontaneous_text}
        </p>
        <Button 
          asChild 
          size="lg" 
          className="bg-white text-[#0078b7] hover:bg-slate-100 font-medium px-8 rounded-sm"
        >
          <Link to="/contact">
            Envoyer ma candidature
            <Send className="ml-2 h-5 w-5" />
          </Link>
        </Button>
      </div>
    </section>
  );
};

const RecrutementPage = () => {
  const { content: c } = usePageContent('recrutement', REC_DEFAULTS);
  return (
    <div data-testid="recrutement-page">
      <PageHeader c={c} />
      <PhotoBanner />
      <JobsSection />
      <SpontaneousSection c={c} />
    </div>
  );
};

export default RecrutementPage;
